import { GetServerSideProps, GetStaticPropsContext } from 'next';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import dynamic from 'next/dynamic';

import { getCurrentLocale } from 'utils/getCurrentLocale';
import {
  apolloClient,
  defaultQueryResult,
  getPageQuery,
} from 'utils/apolloClient';

import { SERVICES_QUERY } from '../../graphql/queries';
import { prepareSeoFromPageData } from '../../components/core/SEO/interface';
import { IndexType } from '../../models/pageTypes/services';
import { Page } from '../../models/pageTypes/page';
import Hero from 'components/sections/Hero';
import { Wrapper } from '../../styles/pages/styled/services/services.styles';
import SectionDescriptionWithImage from '../../components/sections/SectionDescriptionWithImage';
import { TitleAs } from '../../models/enums/titleAs';

const DynamicServicesComponent = dynamic(
  () => import('../../components/sections/Services')
);
const DynamicOurTechStack = dynamic(
  () => import('../../components/sections/OurTechStack')
);
const DynamicWhyUs = dynamic(() => import('../../components/sections/WhyUs'));

const Services = ({ data: { page } }: Page<IndexType>) => {
  return (
    <Wrapper>
      <Hero
        title={RichText.asText(page.title)}
        lottieProps={{
          id: 'services_hero_lottie',
          src: 'https://assets2.lottiefiles.com/packages/lf20_yhek11ra.json',
        }}
      />
      <SectionDescriptionWithImage
        title={RichText.asText(page.partner_team.title)}
        subtitle={RichText.asText(page.partner_team.subtitle)}
        description={RichText.asText(page.partner_team.description)}
        image={page.partner_team.image}
        isImageToRight
        titleAs={TitleAs.h2}
      />

      <DynamicServicesComponent services={page.services} />

      <DynamicOurTechStack
        title={RichText.asText(page.our_tech_stack.title)}
        description={RichText.asText(page.our_tech_stack.description)}
        ourTechStackIcons={page.our_tech_stack.icons}
        whatWeCanDoValues={page.our_tech_stack.what_we_can_do_list}
        whatWeCanDoTitle={RichText.asText(
          page.our_tech_stack.what_we_can_do_title
        )}
        titleAs={TitleAs.h4}
      />

      <DynamicWhyUs
        title={RichText.asText(page.why_us_title)}
        reasons={page.why_us_reasons.map((reason) => ({
          title: RichText.asText(reason.reason_title),
          description: RichText.asText(reason.reason_description),
          image: reason.reason_image,
        }))}
        titleAs={TitleAs.h5}
      />
    </Wrapper>
  );
};

export const getStaticProps: GetServerSideProps = async (
  context: GetStaticPropsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  const {
    data: { allServices_pages, ...defaultData },
  } = await apolloClient.query(
    getPageQuery(
      {
        pageLang: currentLanguage,
      },
      'services_query',
      SERVICES_QUERY
    )
  );

  return {
    props: {
      data: {
        ...defaultQueryResult(defaultData),
        seo: prepareSeoFromPageData(allServices_pages.edges[0].node),
        page: allServices_pages.edges[0].node,
      },
    },
  };
};

export default Services;
